import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Calendar, CheckCircle, ChevronRight, Clock, Eye, Package, Search, Truck, XCircle } from "lucide-react";
import blackTshirt from "../../images/shop/black-tshirt.png";
import sneakers from "../../images/shop/sneakers.png";
import chainBag from "../../images/shop/chain-bag.png";
import crossbodyBag from "../../images/shop/crossbody-bag.png";

const Page = styled.main`
  max-width: 1180px;
  margin: 0 auto;
  color: #172033;
`;

const Hero = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 18px;
  padding: 24px 28px;
  border: 1px solid #e4edf8;
  border-radius: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #eef6ff 100%);
  box-shadow: 0 12px 34px rgba(40, 102, 181, 0.07);

  span {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #2f80ed;
    font-size: 13px;
    font-weight: 900;
  }

  h1 {
    margin: 8px 0;
    font-size: 30px;
  }

  p {
    margin: 0;
    color: #66758f;
  }
`;

const Filter = styled.section`
  display: grid;
  grid-template-columns: 1fr 150px 150px;
  gap: 10px;
  margin-bottom: 18px;
  padding: 14px;
  border: 1px solid #e4edf8;
  border-radius: 8px;
  background: #fff;
`;

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  height: 42px;
  padding: 0 13px;
  border: 1px solid #dce6f2;
  border-radius: 7px;
  color: #8da0b8;

  input {
    width: 100%;
    border: 0;
    outline: 0;
  }
`;

const Select = styled.select`
  height: 42px;
  border: 1px solid #dce6f2;
  border-radius: 7px;
  background: #fff;
  color: #35445c;
  padding: 0 12px;
  font-weight: 800;
`;

const List = styled.section`
  display: grid;
  gap: 16px;
`;

const Card = styled.article`
  border: 1px solid #e3ebf7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 16px 36px rgba(32, 72, 122, 0.07);
  overflow: hidden;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 20px;
  border-bottom: 1px solid #e3ebf7;
  background: #f7fbff;
`;

const OrderNumber = styled.div`
  strong {
    display: block;
    margin-bottom: 5px;
    font-size: 16px;
  }

  span {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #66758f;
    font-size: 13px;
  }
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 8px 12px;
  background: ${(props) => props.$color.background};
  color: ${(props) => props.$color.text};
  font-size: 12px;
  font-weight: 900;
`;

const Body = styled.div`
  padding: 18px 20px;
`;

const Product = styled.div`
  display: grid;
  grid-template-columns: 72px 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 13px 0;
  border-bottom: 1px solid #edf2f8;

  &:last-child {
    border-bottom: 0;
  }

  img {
    width: 72px;
    height: 72px;
    object-fit: contain;
    border: 1px solid #dfe8f4;
    border-radius: 8px;
  }

  strong {
    display: block;
    color: #172033;
  }

  p {
    margin: 5px 0 0;
    color: #66758f;
    font-size: 13px;
  }

  b {
    color: #172033;
  }
`;

const Summary = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-top: 1px solid #e3ebf7;

  strong {
    color: #2f80ed;
    font-size: 20px;
  }

  div {
    display: flex;
    gap: 8px;
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    border: 1px solid #dce6f2;
    border-radius: 7px;
    background: #fff;
    color: #35445c;
    font-weight: 800;
    cursor: pointer;
  }

  button.primary {
    border-color: #2f80ed;
    background: #2f80ed;
    color: #fff;
  }
`;

const Empty = styled.section`
  padding: 70px 24px;
  border: 1px solid #e3ebf7;
  border-radius: 8px;
  background: #fff;
  text-align: center;
  color: #66758f;
`;

const orders = [
  {
    id: "AN-20260501-001",
    date: "2026.05.01",
    status: "delivered",
    products: [
      { name: "로고 크루넥 코튼 티셔츠", option: "블랙 / M", quantity: 1, price: 890000, image: blackTshirt },
      { name: "GG 마틀라세 숄더백", option: "블랙", quantity: 1, price: 2350000, image: chainBag },
    ],
  },
  {
    id: "AN-20260428-014",
    date: "2026.04.28",
    status: "shipped",
    products: [
      { name: "에어 조던 1 로우 블랙 화이트", option: "270", quantity: 1, price: 129000, image: sneakers },
    ],
  },
  {
    id: "AN-20260420-032",
    date: "2026.04.20",
    status: "processing",
    products: [
      { name: "5AC 미니백", option: "블랙", quantity: 1, price: 1290000, image: crossbodyBag },
    ],
  },
];

const statusMap = {
  all: { label: "전체", background: "#f3f4f6", text: "#66758f", icon: Package },
  processing: { label: "상품 준비중", background: "#eaf3ff", text: "#2f70cf", icon: Clock },
  shipped: { label: "배송중", background: "#ecfdf5", text: "#059669", icon: Truck },
  delivered: { label: "배송완료", background: "#dcfce7", text: "#16a34a", icon: CheckCircle },
  cancelled: { label: "주문취소", background: "#fee2e2", text: "#dc2626", icon: XCircle },
};

export default function OrderHistory() {
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("all");
  const [period, setPeriod] = useState("all");

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const target = `${order.id} ${order.products.map((product) => product.name).join(" ")}`.toLowerCase();
      return (status === "all" || order.status === status) && (!keyword || target.includes(keyword.toLowerCase()));
    });
  }, [keyword, status]);

  return (
    <Page>
      <Hero>
        <div>
          <span><Package size={15} /> ORDER HISTORY</span>
          <h1>주문내역</h1>
          <p>주문 상태와 배송 진행 상황을 한눈에 확인하세요.</p>
        </div>
      </Hero>

      <Filter>
        <SearchBox>
          <Search size={17} />
          <input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="주문번호 또는 상품명 검색" />
        </SearchBox>
        <Select value={status} onChange={(event) => setStatus(event.target.value)}>
          {Object.entries(statusMap).map(([key, value]) => <option key={key} value={key}>{value.label}</option>)}
        </Select>
        <Select value={period} onChange={(event) => setPeriod(event.target.value)}>
          <option value="all">전체 기간</option>
          <option value="1m">최근 1개월</option>
          <option value="3m">최근 3개월</option>
          <option value="6m">최근 6개월</option>
        </Select>
      </Filter>

      {filteredOrders.length === 0 ? (
        <Empty>조건에 맞는 주문내역이 없습니다.</Empty>
      ) : (
        <List>
          {filteredOrders.map((order) => {
            const currentStatus = statusMap[order.status];
            const Icon = currentStatus.icon;
            const total = order.products.reduce((sum, product) => sum + product.price * product.quantity, 0);
            return (
              <Card key={order.id}>
                <Header>
                  <OrderNumber>
                    <strong>{order.id}</strong>
                    <span><Calendar size={14} /> {order.date}</span>
                  </OrderNumber>
                  <Status $color={currentStatus}><Icon size={14} /> {currentStatus.label}</Status>
                </Header>
                <Body>
                  {order.products.map((product) => (
                    <Product key={product.name}>
                      <img src={product.image} alt={product.name} />
                      <div>
                        <strong>{product.name}</strong>
                        <p>{product.option} · {product.quantity}개</p>
                      </div>
                      <b>₩ {product.price.toLocaleString("ko-KR")}</b>
                    </Product>
                  ))}
                </Body>
                <Summary>
                  <strong>₩ {total.toLocaleString("ko-KR")}</strong>
                  <div>
                    <button type="button"><Eye size={15} /> 상세보기</button>
                    <button type="button" className="primary">다시 주문 <ChevronRight size={15} /></button>
                  </div>
                </Summary>
              </Card>
            );
          })}
        </List>
      )}
    </Page>
  );
}
